import { Box, Link, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { GridColDef } from "@mui/x-data-grid";
import moment, { isMoment } from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import useGet from "../../../services/hooks/useGet";
import usePost from "../../../services/hooks/usePost";
import { ICase } from "../../../services/interfaces/DataInterfaces";
import { apiEndpoints } from "../../../shared/constants/api-constant";
import {
  AlertType,
  EComponentType,
  ICONS,
} from "../../../shared/constants/enums";
import {
  Button,
  Heading,
  SelectInput,
} from "../../../shared/ReusableComponents";
import ArrowBack from "../../../shared/ReusableComponents/ArrowBack";
import CircularProgressDialog from "../../../shared/ReusableComponents/CircularProgressDialog";
import { DateTimeInput } from "../../../shared/ReusableComponents/DateTimeInput";
import { GridTable } from "../../../shared/ReusableComponents/GridTable";
import { CommonFunctions } from "../../../shared/utils/CommonFunctions";
import {
  DateFormatter,
  DateTimeFormats,
} from "../../../shared/utils/DateFormatter";
import { hideLoader, showLoader } from "../../../store/currentPreferencesSlice";
import { toast } from "../../../store/toastSlice";
import PatientCard from "../../common/CaseDetails/PatientCard";

export const AssignStudent = () => {
  const { currentTemplateId } = useParams<{
    currentTemplateId: number | any;
  }>();

  const dispatch = useDispatch();
  const [patientData, setPatientData] = useState<ICase | any>();
  const [cohortData, setCohortData] = useState<any>([]);
  const [submitted, setSubmitted] = useState(false);
  const [isUpload, setIsUpload] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<any>({
    date: "",
    cohort: "",
  });
  const [circularDialogOpen, setCircularDialogOpen] = useState(false);
  const [forms, setForms] = useState<any>({
    templateId: currentTemplateId,
    effectiveDate: "",
    // students: null,
    cohortId: "",
  });
  const {
    loading: patientLoading,
    data: patientDetails,
    getService: patientService,
  } = useGet();
  const {
    loading: coHortLoading,
    data: coHortDetails,
    getService: coHortService,
  } = useGet();
  const {
    data: assignTemplateStatusData,
    getService: assignTemplateStatusService,
  } = useGet();

  const {
    loading: assignLoading,
    data: assignRes,
    postService: assignService,
  } = usePost();

  const auth = useSelector((state: any) => state.login);
  const [gridData, setGridData] = useState<any[]>([]);
  const navigate = useNavigate();

  const onChangeValues = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: string
  ) => {
    if (field === "effectiveDate") {
      const date = isMoment(e) && DateFormatter.formatDate(e.toISOString());
      if (date === "Invalid date" || date === "" || date === false) {
        setForms({ ...forms, [field]: "" });
      } else setForms({ ...forms, [field]: date });
    }
    if (field === "cohortName") {
      setForms({ ...forms, cohortId: e.target.value });
    }
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    setIsUpload(true);
    setCircularDialogOpen(true);
    const payload = {
      templateId: Number(forms.templateId),
      effectiveDate: moment
        .utc(forms.effectiveDate, "DD/MM/YYYY")
        .local(true)
        .toISOString(),
      cohortId: Number(forms.cohortId),
    };

    assignService(`${apiEndpoints.ASSIGNTEMPLATE}`, payload);
  };

  useEffect(() => {
    if (assignRes) {
      dispatch(
        toast({
          message: "The assignment is completed. Please review the Result.",
          type: AlertType.SUCCESS,
        })
      );
    }
  }, [assignRes]);

  const selectedCohortName =
    cohortData.find((c: any) => c.value === Number(forms["cohortId"]))?.name ||
    "";
  const displayId = CommonFunctions.ConvertIdToDisplayId(
    currentTemplateId,
    "template"
  );
  const downloadFileName = `${displayId}_${selectedCohortName}__Student_Assignment_Status.csv`;

  const downloadCSV = (fileName: string) => {
    if (!assignTemplateStatusData || assignTemplateStatusData.length === 0)
      return;
    const arr = Array.isArray(assignTemplateStatusData)
      ? assignTemplateStatusData
      : [assignTemplateStatusData];

    const csvData = arr.map((item: any) => ({
      "Template ID": CommonFunctions.ConvertIdToDisplayId(
        item.templateId,
        "template"
      ),
      "Student Email ID": item.email,
      Status: item.status === "Success" ? "Success" : "Failed",
      "Case ID":
        item.status === "Success"
          ? CommonFunctions.ConvertIdToDisplayId(item.caseId, "case")
          : "-",
      "Reasons for failure":
        item.status === "Success" ? "-" : item.reason || "-",
    }));

    const csv = CommonFunctions.convertToCSV(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const name = `${fileName.split(".")[0]}_${DateFormatter.formatDate(
      new Date().toISOString()
    )}.csv`;

    CommonFunctions.downloadFileFromLink(url, downloadFileName);
    URL.revokeObjectURL(url);
  };

  const renderFile = (params: any) => {
    return (
      <Link
        underline="always"
        sx={{ cursor: "pointer" }}
        onClick={() => {
          downloadCSV(downloadFileName);
        }}
      >
        <Tooltip title={params.row.fileName}>
          <span>Download CSV</span>
        </Tooltip>
      </Link>
    );
  };

  const columns: GridColDef[] = [
    {
      field: "templateId",
      headerName: "Template ID",
    },
    {
      field: "effectiveDate",
      headerName: "Effective Date",
    },
    {
      field: "cohortName",
      headerName: "Cohort Name",
    },
    {
      field: "noofStudentsUpload",
      headerName: "No of Students Assigned",
    },
    {
      field: "success",
      headerName: "Success",
    },
    {
      field: "failed",
      headerName: "Failed",
    },
    {
      field: "uploadedBy",
      headerName: "Assigned by",
    },
    {
      field: "uploadResult",
      headerName: "Upload Result",
      renderCell: renderFile,
    },
  ];

  useEffect(() => {
    patientService(`${apiEndpoints.TEMPLATE}/${currentTemplateId}`);
  }, [currentTemplateId]);

  useEffect(() => {
    patientDetails && setPatientData(patientDetails);
  }, [patientDetails]);

  useEffect(() => {
    patientLoading && dispatch(showLoader());
    !patientLoading && dispatch(hideLoader());
  }, [patientLoading]);

  useEffect(() => {
    coHortService(`${apiEndpoints.COHORT}`);
  }, [currentTemplateId]);

  useEffect(() => {
    if (coHortDetails && coHortDetails.length > 0) {
      const newCohortData = coHortDetails.map((cohort: any) => ({
        name: cohort.cohortName,
        value: cohort.id,
      }));
      setCohortData(newCohortData);
    } else setCohortData([]);
  }, [coHortDetails]);

  useEffect(() => {
    if (assignRes && assignRes?.id) {
      assignTemplateStatusService(
        `${apiEndpoints.ASSIGNTEMPLATE_STATUS}/${assignRes.id}`
      );
    }
  }, [assignRes]);

  useEffect(() => {
    if (assignTemplateStatusData) {
      setCircularDialogOpen(false);
      const arr = Array.isArray(assignTemplateStatusData)
        ? assignTemplateStatusData
        : [assignTemplateStatusData];

      const gridSample = {
        id: 1,
        templateId: CommonFunctions.ConvertIdToDisplayId(
          currentTemplateId,
          "template"
        ),
        effectiveDate: forms["effectiveDate"],
        fileName: downloadFileName,
        noofStudentsUpload: arr.length,
        success: arr.filter((a) => a.status === "Success").length,
        failed: arr.filter((a) => a.status !== "Success").length,
        uploadedBy: CommonFunctions.formatFirstAndLastName(
          auth?.userInfo?.userFirstName,
          auth?.userInfo?.userLastName
        ),
        uploadResult: "success",
        cohortName:
          cohortData.find((c: any) => c.value === Number(forms["cohortId"]))
            ?.name || "",
      };
      setGridData([gridSample]);
    }
  }, [assignTemplateStatusData]);

  useEffect(() => {
    coHortLoading && dispatch(showLoader());
    !coHortLoading && dispatch(hideLoader());
  }, [coHortLoading]);

  useEffect(() => {
    !assignLoading && dispatch(hideLoader());
  }, [assignLoading]);

  const handleBack = () => {
    navigate("/admin/templatelibrary");
  };

  return (
    <React.Fragment>
      <ArrowBack className="h3" text="Assign to Students" />
      <Box className="box-grid assign-student">
        {patientData && (
          <Box sx={{ display: "flex" }}>
            <Heading
              isBold={true}
              text={`${CommonFunctions.ConvertIdToDisplayId(
                patientData.id,
                "template"
              )},`}
              type="h5"
            />
            &nbsp;&nbsp;
            <Heading
              isBold={true}
              text={`${
                patientData.templateDescription.length <= 100
                  ? patientData.templateDescription
                  : `${patientData.templateDescription.substring(0, 100)}...`
              }`}
              type="h5"
              title={patientData.templateDescription}
            />
          </Box>
        )}
        {patientData && (
          <PatientCard
            data={patientData}
            type={EComponentType.assignStudents}
          />
        )}
        <div className={`${isUpload ? "upload-opacity-box" : ""}`}>
          <Heading
            isBold={true}
            type="h6"
            text="To assign a case template, select the effective date, choose a cohort, and click Assign to Students."
          />
        </div>
        <Grid
          container
          className={`mt-1 ${isUpload ? "upload-opacity-box" : ""}`}
        >
          <Grid size={3}>
            <DateTimeInput
              type="date"
              name="Effective Date"
              format={DateTimeFormats.DateFormat}
              value={forms["effectiveDate"]}
              onChange={(e: any) => onChangeValues(e, "effectiveDate")}
              required={true}
              size="small"
              isDisabled={true}
              hasError={
                submitted && !forms["effectiveDate"] ? errorText.date : false
              }
              label="Effective Date"
              classes="assign-date-picker"
            />
          </Grid>
          <Grid size={0.5}> </Grid>
          <Grid size={3}>
            <SelectInput
              label="Cohort Name"
              required={true}
              name="Cohort Name"
              value={forms["cohortId"]}
              onChange={(e: any) => onChangeValues(e, "cohortName")}
              options={cohortData}
              isValueId={true}
              size="small"
            />
          </Grid>
        </Grid>
        {forms.effectiveDate && forms.cohortId && (
          <Grid className={`${isUpload ? "upload-opacity-box" : ""}`}>
            {!isUpload && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  marginTop: "1rem",
                }}
              >
                <Button
                  id="close"
                  type="button"
                  variant="outlined"
                  fullWidth={false}
                  text="Cancel"
                  // className="secondaryBtn"
                  onClick={handleBack}
                />
                <Button
                  id="submit"
                  fullWidth={false}
                  className="ml-1"
                  onClick={handleSubmit}
                  text="Assign to Students"
                />
              </Box>
            )}
          </Grid>
        )}
        {isUpload && (
          <Grid className="data-Grid mt-1">
            <Heading isBold={true} text="Result:" type="h5" />
            <GridTable
              rows={gridData}
              columns={columns}
              isPaginationHide={true}
            />
          </Grid>
        )}
      </Box>
      <CircularProgressDialog
        text="Note: Case template is being assigned to the selected cohort. Please do not refresh or exit this page. A confirmation message will appear shortly."
        dialogOpen={circularDialogOpen}
        icon={ICONS.WARNING}
      />
    </React.Fragment>
  );
};
